import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalRConnection } from '@/core/realtime/public';
import { env } from '@/core/config/env';
import { TASK_KEYS } from '@/features/tasks/public';
import { NOTIFICATION_KEYS } from './queryKeys';
import type { NotificationDto } from '../types';

/**
 * Subscribes to the notifications SignalR hub.
 *
 * Delivery is per-user, not per-organization — the hub groups connections by
 * `user:{userId}` server-side (resolved from the JWT), so a notification from
 * any of the user's tenants arrives here regardless of which org is active in
 * the UI. The connection itself carries no org context.
 *
 * On ReceiveNotification: invalidates the user-global notification list so
 * every active filter view (unread, all) re-fetches and the badge updates.
 * For task-related events, also invalidates that task's own tenant's task
 * queries (using the notification's `tenantId`, not necessarily the active
 * org) to reflect the change.
 * On reconnect: invalidates the full list to reconcile any missed events.
 */
export function useNotificationHub() {
  const queryClient = useQueryClient();

  const hubBaseUrl = env.VITE_SIGNALR_HUB_URL;
  const hubUrl = hubBaseUrl ? `${hubBaseUrl}/hubs/notifications` : '';

  const { connectionRef, status, reconnectCount } = useSignalRConnection({
    hubUrl,
    enabled: !!hubUrl,
  });

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection) return;

    const handleReceive = (notification: NotificationDto) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });

      const tenantId = notification.tenantId;
      if (notification.type?.startsWith('task_') && tenantId) {
        queryClient.invalidateQueries({ queryKey: TASK_KEYS.myPendingTasks(tenantId) });
        if (notification.entityId) {
          queryClient.invalidateQueries({
            queryKey: TASK_KEYS.detail(tenantId, notification.entityId),
          });
        }
      }
    };

    connection.on('ReceiveNotification', handleReceive);
    return () => {
      connection.off('ReceiveNotification', handleReceive);
    };
  }, [connectionRef, queryClient]);

  useEffect(() => {
    if (reconnectCount === 0) return;
    queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
  }, [reconnectCount, queryClient]);

  return { connectionRef, status, reconnectCount };
}
